'use strict';

import _ from 'lodash';

import { EventEmitter } from 'events';

import CKError from '../classes/Error';

export default class ModuleProvider extends EventEmitter {
	constructor(parent) {
		super();

		this._parent = parent;
		if (parent) {
			this.provideModule(parent);
		}
		this.provideSuperModule(this);
	}

	module(nameOrClass) {
		let name = this.getNameForModule(nameOrClass);
		const module =
			_.get(this, ['_modules', name]) || (this._parent ? this._parent.module(name) : null);

		if (!module) {
			if (typeof window !== 'undefined' && _.get(window, 'CK.moduleContainer')) {
				const legacyModule = CK.moduleContainer.module(nameOrClass);

				if (legacyModule) {
					CK.logger.trace('Falling back to legacy module', {
						nameOrClass
					});
					return legacyModule;
				}
			}

			CK.logger.warn('Missing module for name', {
				name
			});
		}

		return module;
	}

	off(...args) {
		this.removeListener(...args);
	}

	newInstance(nameOrClass, ...args) {
		let Module = this.module(nameOrClass);

		if (Module.default) {
			Module = Module.default;
		}

		return new Module(...args);
	}

	provideSuperModule(module) {
		let constructor = _.get(module, 'constructor') || _.get(module, 'prototype.constructor');

		while (constructor.name) {
			this.provideModule(constructor, module);
			constructor = Object.getPrototypeOf(constructor);
		}
	}

	getNameForModule(name) {
		if (!_.isString(name)) {
			name =
				// custom name
				_.get(name, 'NAME') ||
				// es6 class
				_.get(name, 'prototype.constructor.name') ||
				// es6 class using require
				_.get(name, 'default.prototype.constructor.name') ||
				// es6 instance
				_.get(name, 'constructor.name') ||
				// Extendable instance
				_.get(name, 'type');
		}

		if (!name) {
			CK.logger.warn("Can't resolve module name", name);
		}

		return name;
	}

	provideModule(a, b) {
		let name = this.getNameForModule(a);
		let object = b;

		if (!b) {
			object = a;
		}

		if (!this._modules) {
			this._modules = {};
		}

		if (!name) {
			throw new CKError("Can't provide a module with an invalid name", {
				name,
				object
			});
		}
		this._modules[name] = object;

		return object;
	}

	provideServices(services) {
		_.each(services, (service, name) => {
			this.provideModule(name, service);
		});

		if (this._parent) {
			this._parent.provideServices(services);
		}
	}

	mergeState(delta) {
		const customiser = function(oldValue, newValue) {
			if (_.isArray(newValue)) {
				return newValue;
			}
		};

		this.setState(_.mergeWith({}, this.state, delta, customiser));
	}

	setState(delta) {
		if (typeof delta === 'function') {
			delta = delta(this.state);
		}

		if (this.destroyed) {
			const CKError = require('../classes/Error');
			throw new CKError("Can't set state on a destroyed module provider");
		}

		const oldState = this.state;
		this.state = _.extend({}, this.state, delta);

		if (this._reactSubscribers) {
			for (let [reactInstance, mapping, filter] of this._reactSubscribers) {
				const partialState = {};
				let changed = false;

				for (let key in mapping) {
					if (!delta.hasOwnProperty(key)) {
						continue;
					}
					partialState[mapping[key]] = this.state[key];
					changed = true;
				}

				if (changed) {
					reactInstance.setState(
						prevState => (filter ? filter(prevState, partialState) : partialState)
					);
				}
			}
		}

		this.emit(ModuleProvider.EVENTS.UPDATE_STATE, this.state, oldState);
	}

	subscribeState(reactComponentInstance, reactToProviderStateMapping, filter) {
		if (!this._reactSubscribers) {
			this._reactSubscribers = [];
		}

		reactComponentInstance.setState(
			_.mapValues(reactToProviderStateMapping, key => this.state[key])
		);

		this._reactSubscribers.push([reactComponentInstance, reactToProviderStateMapping, filter]);
	}

	unsubscribeState(reactComponentInstance) {
		this._reactSubscribers = _.filter(this._reactSubscribers, entry => {
			return entry[0] !== reactComponentInstance;
		});
	}

	createGuardedProcess(fns, options) {
		options = _.defaults(options || {}, {
			forceSupersede: false,
			invalidateIfFulfilled: false,
			cancelOnDestroy: true
		});

		if (!_.isArray(fns)) {
			fns = [fns];
		}

		const GuardedProcess = require('./GuardedProcess');
		let process = new GuardedProcess(fns);

		if (options.cancelOnDestroy) {
			if (!this._containers) {
				this._containers = [];
			}

			// Add a container with this fn so it is destroyed
			this._containers.push([process]);
		}

		let fn = (...args) => {
			if (options.invalidateIfFulfilled) {
				const exec = process.getExecution();

				if (exec && exec.isFulfilled) {
					process.invalidate();
				}
			}

			if (options.forceSupersede) {
				return process.supersede(this, ...args);
			} else {
				return process.getOrStart(this, ...args);
			}
		};
		fn.process = process;
		return fn;
	}

	createChildContainer(container) {
		if (!this._containers) {
			this._containers = [];
		}

		this._containers.push(container);

		return container;
	}

	destroy() {
		if (this._containers) {
			for (let container of this._containers) {
				_.invokeMap(container, 'destroy');
			}
		}

		this.destroyed = true;

		this.removeAllListeners();

		if (this._reactSubscribers) {
			this._reactSubscribers.length = 0;
		}
		if (this._modules) {
			this._modules.length = 0;
		}
	}

	setMeasure(name, timeout) {
		const perf = this.module('ClientPerformanceMonitor');

		if (perf) {
			const currentId = this[`_measure-${name}`];

			if (currentId) {
				perf.cancelMeasure(currentId);
			}

			this[`_measure-${name}`] = perf.startMeasure(name, timeout);
		}
	}

	clearMeasure(name, waitForDraw = true) {
		const perf = this.module('ClientPerformanceMonitor');

		if (perf) {
			const currentId = this[`_measure-${name}`];

			if (waitForDraw) {
				requestAnimationFrame(() => {
					perf && perf.endMeasure(currentId);
				});
			} else {
				perf.endMeasure(currentId);
			}
		}
	}

	cancelMeasure(name) {
		const perf = this.module('ClientPerformanceMonitor');

		if (perf) {
			const currentId = this[`_measure-${name}`];

			perf.cancelMeasure(currentId);
		}
	}
}

ModuleProvider.EVENTS = {
	UPDATE_STATE: 'updateState'
};
